import React, { useState, useEffect } from 'react';
import api from '../api';
import InvoiceReceiving from './InvoiceReceiving';

const STATUS_COLOR = { pending: 'badge-gray', approved: 'badge-green', rejected: 'badge-red', processed: 'badge-teal' };

export default function InvoiceHistory() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [receivingId, setReceivingId] = useState(null);

  useEffect(() => {
    api.get('/invoices').then(({ data }) => setInvoices(data)).finally(() => setLoading(false));
  }, []);

  async function toggleExpand(inv) {
    if (expanded === inv.id) { setExpanded(null); return; }
    setExpanded(inv.id);
    if (!detail[inv.id]) {
      try {
        const { data } = await api.get(`/invoices/${inv.id}`);
        setDetail(d => ({ ...d, [inv.id]: data }));
      } catch { setDetail(d => ({ ...d, [inv.id]: { error: true } })); }
    }
  }

  if (loading) return <div className="loading">Loading invoices...</div>;
  if (!invoices.length) return (
    <div className="card">
      <div className="card-body text-gray" style={{ textAlign: 'center', padding: 40 }}>
        No invoices yet. Scan a paper invoice with the 📷 scanner to get started.
      </div>
    </div>
  );

  return (
    <div className="card">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Vendor</th>
              <th>Items</th>
              <th>Status</th>
              <th>Scanned By</th>
              <th>Actions</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <React.Fragment key={inv.id}>
                <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpand(inv)}>
                  <td className="text-sm">{new Date(inv.scanned_at).toLocaleDateString()}</td>
                  <td><strong>{inv.vendor_name || inv.vendor || '—'}</strong></td>
                  <td>{inv.line_item_count ?? '—'}</td>
                  <td><span className={`badge ${STATUS_COLOR[inv.status] || 'badge-gray'}`}>{inv.status || 'pending'}</span></td>
                  <td className="text-sm text-gray">{inv.scanned_by_name || '—'}</td>
                  <td>
                    {(inv.status === 'complete' || inv.status === 'pending') && (
                      <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={e => { e.stopPropagation(); setReceivingId(inv.id); }}>
                        Receive
                      </button>
                    )}
                    {inv.status === 'approved' && <span className="badge badge-green" style={{ fontSize: 10 }}>Received</span>}
                  </td>
                  <td style={{ color: '#64748b', fontSize: 12 }}>{expanded === inv.id ? '▲' : '▼'}</td>
                </tr>
                {expanded === inv.id && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '12px 16px' }}>
                        {!detail[inv.id] && <span className="text-gray text-sm">Loading...</span>}
                        {detail[inv.id]?.error && <span className="text-gray text-sm">Failed to load details.</span>}
                        {detail[inv.id] && !detail[inv.id].error && (
                          <table style={{ width: '100%', fontSize: 13 }}>
                            <thead>
                              <tr style={{ color: '#64748b' }}>
                                <th style={{ textAlign: 'left', paddingBottom: 4, fontWeight: 600 }}>Item</th>
                                <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 600 }}>Qty</th>
                                <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 600 }}>Unit</th>
                                <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 600 }}>Unit Price</th>
                                <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 600 }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(detail[inv.id].line_items || []).map((li, i) => (
                                <tr key={i}>
                                  <td style={{ paddingBottom: 2 }}>{li.description || li.name}</td>
                                  <td style={{ textAlign: 'right' }}>{li.quantity ?? '—'}</td>
                                  <td style={{ textAlign: 'right', color: '#64748b' }}>{li.unit || '—'}</td>
                                  <td style={{ textAlign: 'right' }}>{li.unit_price != null ? `$${Number(li.unit_price).toFixed(2)}` : '—'}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{li.total_price != null ? `$${Number(li.total_price).toFixed(2)}` : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {receivingId && (
        <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex justify-between items-center mb-3">
            <span className="font-semibold">Receiving — {invoices.find(i => i.id === receivingId)?.vendor_name}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setReceivingId(null)}>✕ Close</button>
          </div>
          <InvoiceReceiving
            invoiceId={receivingId}
            onComplete={() => {
              setInvoices(prev => prev.map(i => i.id === receivingId ? { ...i, status: 'approved' } : i));
              setReceivingId(null);
            }}
          />
        </div>
      )}
    </div>
  );
}
